/* eslint-disable fp/no-mutation */
import { Button, Size, ControlType, Priority } from '@transferwise/components';
import { formatMoney } from '@transferwise/formatting';
import { CheckCircle } from '@transferwise/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DeliveryEstimation from '../../common/DeliveryEstimation';
import createClassNamesFromStyles from '../../resources/createClassNamesFromStyles';
import createUrl from '../../resources/createUrl';
import styles from '../ProviderComparisonTable.module.less';

const createClassNames = createClassNamesFromStyles(styles);

const COMPARATORS = {
  BETTER: 'BETTER',
  SAME: 'SAME',
  WORSE: 'WORSE',
};

const SPEED_OPTIONS = {
  BETTER: 'speed-better',
  SAME: 'speed-same',
  WORSE: 'speed-worse',
};

const COST_OPTIONS = {
  BETTER: 'cost-better',
  SAME: 'cost-same',
  WORSE: 'cost-worse',
};

// Generates "Save 10 GBP", "Get 123.23 EUR", "3 USD more"
const renderSavings = (savings, receivedAmount, targetCurrency, locale, costComparison) => {
  if (!costComparison) {
    return null;
  }
  const format = (amount) => (
    <span className={createClassNames(['font-weight-bold', 'text-xs-nowrap'])}>
      {formatMoney(amount, targetCurrency, locale)}
    </span>
  );

  let amount;
  if (COMPARATORS.BETTER === costComparison) {
    amount = savings;
  }
  if (COMPARATORS.SAME === costComparison) {
    amount = receivedAmount;
  }
  if (COMPARATORS.WORSE === costComparison) {
    amount = savings * -1;
  }
  const messageId = `savings-${costComparison.toLowerCase()}`;
  return <FormattedMessage values={{ formattedAmount: format(amount) }} id={messageId} />;
};

// prettier-ignore
const generateCtaUrl = (sendAmount, sourceCurrency, targetCurrency) => createUrl(
  '/'
    + `?sourceAmount=${sendAmount}`
    + `&sourceCurrency=${sourceCurrency}`
    + `&targetCurrency=${targetCurrency}`,
);

const SendWithTw = ({
  deliveryEstimate,
  speedComparison,
  savings,
  receivedAmount,
  costComparison,
  supportedRoute,
  sendAmount,
  sourceCurrency,
  targetCurrency,
  showDeliveryEstimates,
  onCallToActionClick,
}) => {
  const { formatMessage, locale } = useIntl();
  // prettier-ignore
  const same = speedComparison === COMPARATORS.SAME
    && costComparison === COMPARATORS.SAME;
  // prettier-ignore
  const worse = speedComparison === COMPARATORS.WORSE
    && costComparison === COMPARATORS.WORSE;

  const buttonProps = {};
  if (supportedRoute) {
    buttonProps.label = formatMessage({ id: 'cta-for-supported-route' });
    buttonProps.url = generateCtaUrl(sendAmount, sourceCurrency, targetCurrency);
  } else {
    buttonProps.label = formatMessage({ id: 'cta-for-unsupported-route' });
    buttonProps.url = 'https://wise.com/wishes';
  }

  return (
    <div className={createClassNames(['CompareCard'])}>
      <div className={createClassNames(['CompareCard__figure'])}>
        <img
          src="https://wise.com/public-resources/assets/logos/wise-personal/logo.svg"
          alt={formatMessage({ id: 'wise-logo-alt-text' })}
        />
      </div>
      {supportedRoute && (
        <>
          {!worse && !same && (
            <>
              {showDeliveryEstimates && (
                <div className={createClassNames(['CompareCard__item'])}>
                  <span className={createClassNames(['CompareCard__text'])}>
                    <span
                      className={createClassNames(['CompareCard__label'])}
                      data-testid="cost-comparator"
                    >
                      <CheckCircle />

                      {`${
                        SPEED_OPTIONS[speedComparison]
                          ? formatMessage({
                              id: SPEED_OPTIONS[speedComparison],
                            }) + ','
                          : ''
                      } ${formatMessage({ id: COST_OPTIONS[costComparison] })}`}
                    </span>
                    <span
                      className={`${styles['font-weight-bold']} ${createClassNames([
                        'CompareCard__text__delivery-estimation',
                      ])}`}
                    >
                      {deliveryEstimate && (
                        <DeliveryEstimation
                          deliveryEstimation={deliveryEstimate}
                          alias="transferwise"
                          onClickingDeliveryEstimationTooltip={() => {}}
                          isPartner={false}
                          styles={styles}
                        />
                      )}
                    </span>
                  </span>
                </div>
              )}
              <div className={createClassNames(['CompareCard__item'])}>
                <span className={createClassNames(['CompareCard__text'])} data-testid="savings">
                  {renderSavings(savings, receivedAmount, targetCurrency, locale, costComparison)}
                </span>
              </div>
            </>
          )}
          {worse && (
            <div className={createClassNames(['CompareCard__item'])}>
              {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
              <span className={createClassNames(['CompareCard__text', 'text-xs-center', 'p-r-0'])}>
                {formatMessage({ id: 'compare-worse' })}
              </span>
            </div>
          )}

          {same && (
            <div className={createClassNames(['CompareCard__item'])}>
              <span className={createClassNames(['CompareCard__text', 'text-xs-center', 'p-r-0'])}>
                {formatMessage({ id: 'compare-same' })}
              </span>
            </div>
          )}
        </>
      )}

      {!supportedRoute && (
        <div className={createClassNames(['CompareCard__item'])}>
          <span className={createClassNames(['CompareCard__text', 'text-xs-center', 'p-r-0'])}>
            {formatMessage({ id: 'route-unsupported' })}
          </span>
        </div>
      )}

      <a
        href={buttonProps.url}
        data-testid="comparison-cta"
        data-tracking-id="comparison-cta"
        data-tracking-targetcurrency={targetCurrency}
        data-tracking-cost-comparison={costComparison}
        data-tracking-speed-comparison={speedComparison}
        data-tracking-savings={savings}
        className={createClassNames(['send-with-tw-cta'])}
      >
        <Button
          size={Size.SMALL}
          type={ControlType.POSITIVE}
          priority={Priority.PRIMARY}
          block
          className={createClassNames(['send-with-tw-cta'])}
          onClick={onCallToActionClick}
        >
          {buttonProps.label}
        </Button>
      </a>
    </div>
  );
};

SendWithTw.propTypes = {
  costComparison: PropTypes.oneOf(Object.keys(COMPARATORS)),
  speedComparison: PropTypes.oneOf(Object.keys(COMPARATORS)),
  supportedRoute: PropTypes.bool,
  sendAmount: PropTypes.number,
  sourceCurrency: PropTypes.string,
  targetCurrency: PropTypes.string,
  deliveryEstimate: PropTypes.shape({}),
  savings: PropTypes.number,
  receivedAmount: PropTypes.number,
  showDeliveryEstimates: PropTypes.bool,
  onCallToActionClick: PropTypes.func,
};

SendWithTw.defaultProps = {
  costComparison: 'BETTER',
  speedComparison: 'BETTER',
  supportedRoute: false,
  sendAmount: 0,
  sourceCurrency: null,
  targetCurrency: null,
  deliveryEstimate: null,
  savings: 0,
  receivedAmount: 0,
  showDeliveryEstimates: false,
  onCallToActionClick: () => {},
};

export default SendWithTw;
